/**
 * Files (Aziz 2026-09-28/29): an executive, Drive-style view of one clinic. One folder
 * per step, named as the creative's Progress bar names it, then the creatives on that
 * step, then the creative's own Assets card: the same file cards, reviews and comments
 * as on the creative page. Plus the clinic's own uploads.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ExternalLink, Folder, Upload } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/cn';
import { qk, fetchCreative, fetchDrive, fetchDriveFolder, type DriveCreative } from '@/lib/queries';
import AssetsPanel from '@/components/creative/AssetsPanel';

//: what a creative holds, in the words its steps use (never "cuts")
const KIND_LABEL: Record<string, string> = { raw: 'raws', edit: 'edits', final: 'final' };

function FolderTile({ label, count, sub, onClick, testid, tone }: { label: string; count?: number; sub?: string; onClick: () => void; testid: string; tone?: 'muted' }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={cn('group text-left rounded-xl border border-slate-800 bg-bg-card/40 hover:border-accent-400/60 hover:bg-bg-card p-3 flex flex-col gap-2 min-h-[108px]',
        tone === 'muted' && count === 0 && 'border-dashed')}>
      <div className="flex items-start justify-between gap-2">
        <Folder size={30} className="text-amber-300/80 group-hover:text-amber-300 shrink-0" fill="currentColor" fillOpacity={0.15} />
        {count !== undefined && <span className="text-xs tabular-nums text-muted-strong">{count}</span>}
      </div>
      <div className="min-w-0">
        <div className="text-sm text-muted-strong truncate" dir="auto">{label}</div>
        {sub && <div className="text-[11px] text-muted truncate" dir="auto">{sub}</div>}
      </div>
    </button>
  );
}

export default function ClinicFolders({ clientId, clientName, onOpenUploads }: { clientId: string; clientName: string; onOpenUploads: () => void }) {
  const [folder, setFolder] = useState<{ key: string; label: string } | null>(null);
  const [creative, setCreative] = useState<DriveCreative | null>(null);
  const top = useQuery({ queryKey: ['drive', clientId], queryFn: () => fetchDrive(clientId) });
  const inFolder = useQuery({ queryKey: ['drive', clientId, folder?.key], queryFn: () => fetchDriveFolder(clientId, folder!.key), enabled: !!folder });
  const full = useQuery({ queryKey: qk.creative(creative?.id ?? ''), queryFn: () => fetchCreative(creative!.id), enabled: !!creative });

  const crumbs = (
    <nav className="flex flex-wrap items-center gap-1 text-sm mb-4" aria-label="Folders" data-testid="drive-crumbs">
      <button type="button" onClick={() => { setFolder(null); setCreative(null); }} className={cn(folder ? 'text-accent-400 hover:underline' : 'text-muted-strong')}>{clientName}</button>
      {folder && <><ChevronRight size={14} className="text-muted" />
        <button type="button" onClick={() => setCreative(null)} className={cn(creative ? 'text-accent-400 hover:underline' : 'text-muted-strong')}>{folder.label}</button></>}
      {creative && <><ChevronRight size={14} className="text-muted" /><span className="text-muted-strong">{creative.code}</span></>}
    </nav>
  );

  if (creative) {
    return (
      <div data-testid="drive-files">
        {crumbs}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="min-w-0">
            <div className="text-sm text-muted-strong truncate" dir="auto">{creative.title}</div>
            <div className="text-[11px] text-muted">{creative.code} · on {folder?.label}</div>
          </div>
          <Link to={`/creatives/${creative.id}`} className="inline-flex items-center gap-1 text-xs text-accent-400 hover:underline"><ExternalLink size={12} /> Open the creative</Link>
        </div>
        {full.isLoading && <div className="text-sm text-muted">Loading files...</div>}
        {full.data && <AssetsPanel creative={full.data} alwaysOpen />}
      </div>
    );
  }

  if (folder) {
    return (
      <div data-testid="drive-folder">
        {crumbs}
        {inFolder.isLoading && <div className="text-sm text-muted">Loading...</div>}
        {inFolder.isSuccess && inFolder.data.length === 0 && <Card><p className="text-sm text-muted">No creatives in {folder.label} right now.</p></Card>}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {(inFolder.data ?? []).map((c) => {
            const n = Object.values(c.files).reduce((a, b) => a + b, 0);
            const parts = Object.entries(c.files).filter(([k]) => KIND_LABEL[k]).map(([k, v]) => `${v} ${KIND_LABEL[k].toLowerCase()}`);
            return <FolderTile key={c.id} label={c.title || c.code} sub={`${c.code} · ${n ? parts.join(', ') || `${n} files` : 'no files yet'}`}
              onClick={() => setCreative(c)} testid="drive-creative" />;
          })}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="drive-top">
      {crumbs}
      {top.isLoading && <div className="text-sm text-muted">Loading folders...</div>}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {(top.data?.folders ?? []).map((f) => (
          <FolderTile key={f.key} label={f.label} count={f.count} sub={`${f.count} creative${f.count === 1 ? '' : 's'}`} tone="muted"
            onClick={() => setFolder(f)} testid={`drive-folder-${f.key}`} />
        ))}
        {top.data && (
          <button type="button" onClick={onOpenUploads} data-testid="drive-folder-uploads"
            className="text-left rounded-xl border border-dashed border-slate-700 bg-bg-card/20 hover:border-accent-400/60 p-3 flex flex-col gap-2 min-h-[108px]">
            <div className="flex items-start justify-between gap-2"><Upload size={26} className="text-sky-300/80" /><span className="text-xs tabular-nums text-muted-strong">{top.data.uploads}</span></div>
            <div><div className="text-sm text-muted-strong">Clinic uploads</div><div className="text-[11px] text-muted">Raw material the clinic sent</div></div>
          </button>
        )}
      </div>
    </div>
  );
}
